// frontend/src/pages/ResetPasswordPage/ResetPasswordPage.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import styles from '../LoginPage/LoginPage.module.css'; // Shared auth page styles

import FormInput from '../../components/FormInput/FormInput';
import Button from '../../components/Button/Button';
import { resetPassword } from '../../services/authService'; // Import the service

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // State for URL parameters
  const [token, setToken] = useState('');
  const [usernameFromUrl, setUsernameFromUrl] = useState('');

  // Form state
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // UI feedback state
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    const userFromUrl = searchParams.get('username'); // Corrected variable name for consistency

    if (tokenFromUrl) setToken(tokenFromUrl);
    if (userFromUrl) setUsernameFromUrl(userFromUrl);

    if (!tokenFromUrl || !userFromUrl) {
      setError('Invalid or missing password reset parameters. Please use the link from your email.');
      // The UI below will also reflect this error state by not showing the form.
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (!token || !usernameFromUrl) {
      setError('Missing token or username. Cannot reset password. Please use the link provided in your email.');
      return;
    }

    setLoading(true);

    try {
      const response = await resetPassword({
        token,
        username: usernameFromUrl,
        newPassword
      });
      setMessage(response.message || 'Your password has been reset successfully! Redirecting to login...');
      setNewPassword('');
      setConfirmNewPassword('');
      // Disable the form or inputs after successful submission if desired, before redirect
      // e.g., set a new state variable like `isSuccessfullyReset` to true
      setTimeout(() => navigate('/login'), 3000); // Redirect after a delay
    } catch (apiError) {
      setError(apiError.message || 'Failed to reset password. The link may have expired, been used already, or the new password did not meet requirements.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.authPage}>
      <div className={styles.authCard}>
        <div className={styles.authHeader}>
          <h2 className={styles.authTitle}>Reset Your Password</h2>
          {usernameFromUrl && !error && <p className={styles.authSubtitle}>Enter a new password for user: <strong>{usernameFromUrl}</strong></p>}
        </div>

        <div className={styles.authBody}>
          {/* Show messages only if there isn't a more critical structural error (like missing token) */}
          {message && !error && <div className={styles.infoMessage}>{message}</div>}
          {error && <div className={styles.errorMessage}>{error}</div>}

          {(!token || !usernameFromUrl) && error ? ( // Show this specifically if token/username was missing from URL initially
            <div className={styles.linkContainer}> {/* Added a container for better styling of these links */}
              <p>Please request a new password reset link if needed.</p>
              <Link to="/forgot-password" className={styles.authLink}>Request New Link</Link>
              {' | '}
              <Link to="/login" className={styles.authLink}>Back to Login</Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <FormInput
                label="New Password:"
                id="newPassword"
                name="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  setError(''); // Clear main error on input change
                  setMessage('');
                }}
                placeholder="Enter your new password"
                required
                autoComplete="new-password"
              />
              <FormInput
                label="Confirm New Password:"
                id="confirmNewPassword"
                name="confirmNewPassword"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => {
                  setConfirmNewPassword(e.target.value);
                  setError(''); // Clear main error on input change
                  setMessage('');
                }}
                placeholder="Confirm your new password"
                required
                autoComplete="new-password"
              />
              <Button
                type="submit"
                variant="primary"
                fullWidth
                disabled={loading || !newPassword || !confirmNewPassword || !token || !usernameFromUrl}
                style={{ marginTop: 'var(--space-md)' }}
              >
                {loading ? 'Resetting Password...' : 'Reset Password'}
              </Button>
            </form>
          )}
          {/* General link to login if form is hidden due to success and before redirect */}
          {message && !error && (
            <div className={styles.linkContainer} style={{ marginTop: 'var(--space-md)' }}>
              <Link to="/login" className={styles.authLink}>Proceed to Login</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;